// v1.1 — the tactile tuning panel. Built for "triple-or-third then refine", except the major moves
// are DOUBLE / HALVE (the actual Sid Meier iteration heuristic: make big moves first). Every effect
// gets a ×2 / ÷2 on a normalized intensity knob (1.0 = the tuned baseline); expand for the raw
// params, each with its own ×2 / ÷2, a slider, AND a click-to-type exact value. Every button press
// fires a random word so you feel the change without retyping. Plus a completely-random dice roll
// and a continuous auto-fire loop.

import { feel, DEFAULT_FEEL } from "../juice/feel";
import { PARAM_HINTS, WAVEFORMS, roundToStep, setJuiceSpeed, type Hint } from "./scale";
import { fireOnce, replayLast } from "./fire";

/** A "global speed" row for the TOP of an Advanced list — the same control (÷2 [value] ×2 + slider)
 *  every other dimension uses, but wired to a layered speed multiplier instead of a feel param.
 *  `apply(mult)` sets the rail's speed; it never touches the per-param values below it. Returns the
 *  row plus a `reset()` that snaps it back to 1× (for the rail's Default button). */
export function speedSlider(apply: (mult: number) => void): { el: HTMLElement; reset: () => void } {
  const row = document.createElement("div");
  row.className = "t11-param t11-gspeed";
  const label = document.createElement("label");
  label.textContent = "global speed";
  label.setAttribute(
    "data-tip",
    "Global replay-speed multiplier — scales every time-based effect on this rail at once. " +
      "1× is the tuned baseline; the per-effect values below are left untouched.",
  );
  const state: Record<string, number> = { speed: 1 };
  const localSync: Array<() => void> = [];
  const ctrl = numberControl(state, "speed", { min: 0.25, max: 4, step: 0.05 }, localSync, {
    slider: true,
    apply,
  });
  row.append(label, ctrl.el);
  return {
    el: row,
    reset: (): void => {
      state.speed = 1;
      apply(1);
      localSync.forEach((s) => s());
    },
  };
}

const EFFECTS = [
  "hitstop",
  "sound",
  "tween",
  "countup",
  "screenshake",
  "tilepop",
  "particles",
  "pitchramp",
] as const;

// Plain-language definitions shown on hover over each effect name (a lot of these terms are jargon).
const JUICE_GLOSSARY: Record<string, string> = {
  hitstop: "A tiny freeze on impact (~55ms) — everything stops for a beat, which makes a hit feel weighty.",
  sound: "The synth blips: a bright tick on a valid word, a low buzz on a reject, a flat thud on a flagged one.",
  tween: "Overshoot-and-settle motion — the row punches a touch past its size, then eases back.",
  countup: "The tally rolls up instead of snapping — a number ticking up reads better than one appearing.",
  screenshake: "The whole board jitters on a hit. (This is the shake the footer Calm / Motion toggle controls.)",
  tilepop: "Each letter scales up and settles, staggered, so the word cascades in one tile at a time.",
  particles: "The burst of little specks that flies out from the centre of the word on a hit.",
  pitchramp: "Each letter plays a semitone higher than the last — the word climbs like a do-re-mi ladder.",
};

export function mountV1_1(host: HTMLElement): () => void {
  const syncers: Array<() => void> = [];
  const root = document.createElement("div");
  root.className = "tuner11";

  // ---- header + global controls ----
  root.innerHTML = `
    <div class="t11-head">
      <span class="t11-title">Juice — Under The Hood</span>
      <label class="t11-master"><input type="checkbox" id="t11-master"> MASTER</label>
    </div>
    <div class="t11-hint t11-intro">Eight independent effects hang off one word event — dial each one's punch ×2 / ÷2. <b>New word</b> plays a test word so you feel them, <b>Replay</b> repeats it, <b>Randomize</b> rolls them all. Open Advanced Tuning for the per-effect sliders.</div>
    <div class="t11-global">
      <button id="t11-new" data-tip="Fire a NEW test word so you feel the effects">New word</button>
      <button id="t11-replay" data-tip="Replay the current word at the current juice speed">Replay</button>
    </div>
    <div class="t11-global">
      <span class="t11-row-tag">Juice:</span>
      <button id="t11-dice" class="t11-mini" data-tip="Roll random values for every juice effect, then fire a word so you feel them">Randomize</button>
      <button id="t11-default" class="t11-mini" data-tip="Restore the smart launch defaults for every juice effect (and reset global speed) — always a safe way back">Default</button>
    </div>
    <button class="t11-accordion" id="t11-fx-toggle" aria-expanded="false"><span class="t11-acc-arrow">▾</span> Open Advanced Tuning Controls</button>
    <div class="t11-collapsible" id="t11-fx-wrap" hidden>
      <div class="t11-gwrap" id="t11-gwrap"></div>
      <div class="t11-global-sep"></div>
      <div class="t11-effects"></div>
      <div class="t11-hint">×2 / ÷2 = big moves first (Sid Meier). Click a value to type it exact. Every press fires a word.</div>
    </div>
  `;

  const master = root.querySelector<HTMLInputElement>("#t11-master")!;
  master.checked = feel.master.juice;
  master.addEventListener("change", () => (feel.master.juice = master.checked));

  root.querySelector("#t11-new")!.addEventListener("click", () => fireOnce());
  root.querySelector("#t11-replay")!.addEventListener("click", () => replayLast());
  root.querySelector("#t11-dice")!.addEventListener("click", () => {
    randomizeAll();
    syncers.forEach((s) => s());
    fireOnce();
  });
  // Juice global speed — a layered multiplier on every juice effect's timing (independent of the
  // personality reel). Lives at the TOP of Advanced Tuning as a standard dimension control.
  const gspeed = speedSlider((m) => setJuiceSpeed(m));
  root.querySelector("#t11-gwrap")!.appendChild(gspeed.el);
  // Default — restore the pristine launch feel for every effect + reset global speed. A safe way back.
  root.querySelector("#t11-default")!.addEventListener("click", () => {
    resetToDefault();
    gspeed.reset();
    syncers.forEach((s) => s());
    fireOnce();
  });

  // ---- effects accordion (collapsed by default — mash the big buttons without ever opening it) ----
  const fxToggle = root.querySelector<HTMLButtonElement>("#t11-fx-toggle")!;
  const fxWrap = root.querySelector<HTMLElement>("#t11-fx-wrap")!;
  const fxArrow = fxToggle.querySelector<HTMLElement>(".t11-acc-arrow")!;
  fxToggle.addEventListener("click", () => {
    fxWrap.hidden = !fxWrap.hidden;
    fxToggle.setAttribute("aria-expanded", String(!fxWrap.hidden));
    fxArrow.textContent = fxWrap.hidden ? "▾" : "▴";
  });

  // ---- one flat slider row per effect — matches the Personality rail exactly: label + ÷2/[value]/×2
  //      + slider, no per-effect on/off, no nested expand. The intensity knob (1.0 = baseline) is the
  //      per-effect control (it scales that effect's characteristic punch); MASTER gates the whole rail.
  const effectsHost = root.querySelector<HTMLElement>(".t11-effects")!;
  for (const name of EFFECTS) {
    const cfg = (feel as Record<string, any>)[name];
    const row = document.createElement("div");
    row.className = "t11-param";
    const label = document.createElement("label");
    label.textContent = name;
    if (JUICE_GLOSSARY[name]) label.setAttribute("data-tip", JUICE_GLOSSARY[name]);
    const ctrl = numberControl(cfg, "intensity", PARAM_HINTS.intensity, syncers, { slider: true });
    row.append(label, ctrl.el);
    effectsHost.appendChild(row);
  }

  host.appendChild(root);
  return () => {
    root.remove();
  };
}

/**
 * A number control: [÷2] [editable value] [×2] and (optionally) a slider. Every mutation writes
 * feel, fires a word, and refreshes the display. Registers a syncer so global actions (randomize /
 * reset) can refresh it.
 */
export function numberControl(
  cfg: Record<string, any>,
  key: string,
  hint: Hint | undefined,
  syncers: Array<() => void>,
  opts: { slider: boolean; apply?: (v: number) => void },
): { el: HTMLElement } {
  const wrap = document.createElement("div");
  wrap.className = "t11-num";

  const dn = button("÷2", () => commit(cfg[key] / 2));
  const up = button("×2", () => commit(cfg[key] * 2));

  const input = document.createElement("input");
  input.type = "number";
  input.className = "t11-val";
  if (hint?.step) input.step = String(hint.step);
  input.addEventListener("change", () => commit(parseFloat(input.value)));

  let slider: HTMLInputElement | null = null;
  if (opts.slider && hint) {
    slider = document.createElement("input");
    slider.type = "range";
    slider.min = String(hint.min);
    slider.max = String(hint.max);
    slider.step = String(hint.step ?? (hint.max - hint.min) / 100);
    slider.addEventListener("input", () => write(parseFloat(slider!.value))); // live, no fire
    slider.addEventListener("change", () => commit(parseFloat(slider!.value))); // release → fire
  }

  wrap.append(dn, input, up);
  if (slider) wrap.appendChild(slider);

  function write(v: number): void {
    if (!Number.isFinite(v)) return;
    cfg[key] = roundToStep(v, hint);
    opts.apply?.(cfg[key]); // optional side-effect (e.g. global speed → setJuiceSpeed)
    sync();
  }
  function commit(v: number): void {
    write(v);
    fireOnce();
  }
  function sync(): void {
    input.value = String(cfg[key]);
    if (slider) slider.value = String(cfg[key]);
  }
  sync();
  syncers.push(sync);
  return { el: wrap };
}

function button(text: string, onClick: () => void): HTMLButtonElement {
  const b = document.createElement("button");
  b.className = "t11-x2";
  b.textContent = text;
  b.addEventListener("click", onClick);
  return b;
}

/** Restore every juice effect's params + waveforms to the pristine on-disk defaults (mirrors
 *  randomizeAll's scope — leaves the enable kill-switches alone so their checkboxes stay accurate). */
function resetToDefault(): void {
  for (const name of EFFECTS) {
    const cfg = (feel as Record<string, any>)[name];
    const def = (DEFAULT_FEEL as Record<string, any>)[name];
    for (const key of Object.keys(cfg)) {
      if (key === "enabled") continue;
      if (typeof cfg[key] === "number" || typeof cfg[key] === "string") cfg[key] = def[key];
    }
  }
}

/** Completely random values (NOT influenced by current settings), for every numeric param + wave. */
function randomizeAll(): void {
  for (const name of EFFECTS) {
    const cfg = (feel as Record<string, any>)[name];
    for (const key of Object.keys(cfg)) {
      if (key === "enabled") continue;
      if (typeof cfg[key] === "number") {
        const hint = PARAM_HINTS[key] ?? { min: 0, max: 1 };
        cfg[key] = roundToStep(hint.min + Math.random() * (hint.max - hint.min), hint);
      } else if (typeof cfg[key] === "string") {
        cfg[key] = WAVEFORMS[Math.floor(Math.random() * WAVEFORMS.length)];
      }
    }
  }
}

